'use client';

import { get as _get } from 'lodash-es';
import { useMemo } from 'react';
import Link from 'next/link';
import { useSuspenseQuery, skipToken } from '@apollo/client/react';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import { myGroupsQuery } from '../../apollo/groups';
import { useAppContext } from '../../contexts/app';
import { buildImageUrl } from '../../utils/images';
import { sortGroupsByRelevance } from '../../utils/sort-groups';
import SidebarGroupActions from './sidebar-group-actions';
import SidebarWelcome from './sidebar-welcome';

const SidebarGroups = () => {
  const { isLoggedIn, loggedInUser } = useAppContext();

  const { data: groupsData } = useSuspenseQuery(
    myGroupsQuery,
    isLoggedIn ? {} : skipToken,
  );

  const groups = useMemo(() => {
    if (!groupsData) {
      return [];
    }
    return sortGroupsByRelevance(
      _get(groupsData, 'me.groups', []),
      loggedInUser?.groupMembers,
    );
  }, [groupsData, loggedInUser]);

  if (groups.length === 0) {
    return <SidebarWelcome />;
  }

  return (
    <>
      <Divider />
      <List
        dense
        subheader={
          <ListSubheader
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              lineHeight: '32px',
              pt: 1,
            }}
          >
            My Groups
          </ListSubheader>
        }
      >
        {groups.map((group) => (
          <ListItemButton
            key={group.id}
            component={Link}
            href={`/groups/${group.slug}`}
            dense
          >
            <ListItemAvatar sx={{ minWidth: 36 }}>
              {group.image ? (
                <Avatar
                  alt={group.name}
                  src={buildImageUrl(group.image, '20x20')}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                  {group.name.charAt(0)}
                </Avatar>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={group.name}
              slotProps={{ primary: { noWrap: true, variant: 'body2' } }}
            />
          </ListItemButton>
        ))}
      </List>
      <List dense>
        <SidebarGroupActions browseLabel="Browse All Groups" />
      </List>
    </>
  );
};

export default SidebarGroups;
